import type { FC } from 'react';
import React, { useCallback, useContext, useEffect } from 'react';
import { styled } from '@compiled/react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import SectionMessage from '@atlaskit/section-message/section-message';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	GLOBAL_PAGE_APPS_FORGE_EXPERIENCE,
	ExperienceStart,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { Attribution, ErrorDisplay, withErrorBoundary } from '@confluence/error-boundary';
import { useSessionData } from '@confluence/session-data';

import { getAppId, getModuleKey } from '../../utils';
import { GlobalPageForgeApp } from '../../apps';
import { LazyForgeUIExtensions } from '../../lazy';
import { useProgressLoading } from '../../hooks';
import { FORGE_MODULE_GLOBAL_PAGE } from '../../ForgeModuleType';

const i18n = defineMessages({
	errorMessageTitle: {
		id: 'forge-ui.global.page.extension.point.error.message.title',
		defaultMessage: 'App is not available',
		description: 'Error message title',
	},
	errorMessageDescription: {
		id: 'forge-ui.global.page.extension.point.error.message.description',
		defaultMessage: 'Please contact your site-administrator to install the app.',
		description: 'Error message description',
	},
});

const renderErrorMessage = (error: Error) => {
	return (
		<ErrorDisplay error={error}>
			<SectionMessage appearance="error">
				<FormattedMessage tagName="h4" {...i18n.errorMessageTitle} />
				<FormattedMessage tagName="p" {...i18n.errorMessageDescription} />
			</SectionMessage>
		</ErrorDisplay>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	padding: `${token('space.400', '32px')} ${token('space.400', '32px')}`,
	boxSizing: 'border-box',
});

type GlobalPageProps = {
	appId: string;
	envId: string;
	forgeManifestRoute: string;
};

export const GlobalPage: FC<GlobalPageProps> = withErrorBoundary({
	attribution: Attribution.ECOSYSTEM,
})(({ appId, envId, forgeManifestRoute }) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const [progressLoading, setProgressLoading] = useProgressLoading(true);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { activationId } = useSessionData();

	const onInitialRender = useCallback(
		(app: any) => {
			return () => {
				experienceTracker.succeed({
					name: GLOBAL_PAGE_APPS_FORGE_EXPERIENCE,
				});
				const analyticEvent = createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'invoked',
						actionSubject: 'forgeExtension',
						source: FORGE_MODULE_GLOBAL_PAGE,
						attributes: {
							activationId,
							appId: getAppId(app),
							moduleType: FORGE_MODULE_GLOBAL_PAGE,
							moduleKey: getModuleKey(app),
						},
					},
				});
				analyticEvent.fire();
			};
		},
		[experienceTracker, activationId, createAnalyticsEvent],
	);

	const renderForgeApp = useCallback(
		(apps: any) => {
			const app2render = apps.find(
				(app) =>
					getAppId(app) === appId &&
					app.environmentId === envId &&
					app.properties.route === forgeManifestRoute,
			);

			return app2render ? (
				<GlobalPageForgeApp
					key={appId}
					app={app2render}
					extensionData={{
						type: FORGE_MODULE_GLOBAL_PAGE,
					}}
					onInitialRender={onInitialRender(app2render)}
				/>
			) : (
				renderErrorMessage(new Error(`Global page module was not found`))
			);
		},
		[appId, envId, forgeManifestRoute, onInitialRender],
	);

	useEffect(() => {
		if (progressLoading) setProgressLoading(false);
	}, [progressLoading, setProgressLoading, renderForgeApp]);

	return (
		<Container>
			<ExperienceStart name={GLOBAL_PAGE_APPS_FORGE_EXPERIENCE} id={appId} timeout={10000} />
			<LazyForgeUIExtensions
				moduleType={FORGE_MODULE_GLOBAL_PAGE}
				render={(apps, loading, error) => {
					if (loading) return null;
					if (error) return renderErrorMessage(error);
					return renderForgeApp(apps);
				}}
			/>
		</Container>
	);
});
